import { translateSelector } from '@ackee/jerome';
import { createSelector } from '@reduxjs/toolkit';

import { ListItemType, type SecurityLevel } from '../../constants';
import type { SecurityAuditLevelGroup, SecurityAuditLevelSecret } from '../../types';
import { selectPasswordSecrets } from './list';
import { compareByName, getSecretsWithGroups } from './utilities';

const selecSecurityAuditLevelListSecret = createSelector(selectPasswordSecrets, secrets =>
    secrets.map<SecurityAuditLevelSecret>(secret => ({
        ...secret,
        listItemType: ListItemType.SECRET,
        key: `level-secret-${secret.id}`,
    })),
);

export const selectSecurityAuditLevelSortedListSecrets = createSelector(
    [selecSecurityAuditLevelListSecret, translateSelector],
    (secrets, { locale }) =>
        secrets.sort(function (secretA, secretB) {
            const securityLevelA = secretA.securityLevel;
            const securityLevelB = secretB.securityLevel;

            if (securityLevelA === securityLevelB) {
                return compareByName(secretA, secretB, locale);
            }

            // We want undefined security level to be ordered at the end
            // (the value is "undefined" so it can't be ordered as numbers)
            // @ts-expect-error
            if (securityLevelA < securityLevelB || securityLevelB === undefined) {
                return -1;
            }
            // @ts-expect-error
            if (securityLevelA > securityLevelB || securityLevelA === undefined) {
                return 1;
            }

            return compareByName(secretA, secretB, locale);
        }),
);

const createAuditLevelGroup = (securityLevel: SecurityLevel): SecurityAuditLevelGroup => ({
    listItemType: ListItemType.GROUP,
    key: `level-group-${securityLevel ?? 'noSecurityLevel'}`,
    securityLevel,
    count: 0,
});

export const getSecurityAuditSecretsWithLevelGroups = (secrets: SecurityAuditLevelSecret[]) =>
    getSecretsWithGroups(
        secrets,
        (prevSecret, currentSecret) => prevSecret.securityLevel !== currentSecret.securityLevel,
        // @ts-expect-error
        secret => createAuditLevelGroup(secret.securityLevel),
        group => {
            group.count += 1;
        },
    );

export const selectSecurityAuditSecretsWithLevelGroups = createSelector(
    [selectSecurityAuditLevelSortedListSecrets],
    getSecurityAuditSecretsWithLevelGroups,
);
